export const DETAIL_MODE = {
  CREATE: 'create',
  EDIT: 'edit',
} as const;

export const PO_PERMISSIONS = {
  FINANCE_APPROVER: 'financial-hub/purchase-orders/finance-approver',
  KAM_CREATOR: 'financial-hub/purchase-orders/kam-creator',
  BUSINESS_APPROVER: 'financial-hub/purchase-orders/business-approver',
} as const;
export type DetailMode = typeof DETAIL_MODE[keyof typeof DETAIL_MODE];

// Action identifiers matching the keys in permissions.config.json → "actions".
export const PO_ACTION = {
  EDIT:                  'edit',
  CANCEL_PO:             'cancel_po',
  CLOSE_PO:              'close_po',
  REOPEN_PO:             'reopen_po',
  APPROVE_PO:            'approve_po',
  RETURN_TO_KAM:         'return_to_kam',
  RESUBMIT_FOR_APPROVAL: 'resubmit_for_approval',
  RETRY_SETTLEMENT:      'retry_settlement',
  RETRY_TRATO:           'retry_trato',
} as const;
export type PoAction = typeof PO_ACTION[keyof typeof PO_ACTION];

// Maps the config's role keys (kam_creator, finance_approver, business_approver)
// to the PO_PERMISSIONS slugs used by HasPermissionsService.checkPermission().
export const PO_ROLE_TO_PERMISSION: Record<string, string> = {
  kam_creator:      PO_PERMISSIONS.KAM_CREATOR,
  finance_approver: PO_PERMISSIONS.FINANCE_APPROVER,
  business_approver: PO_PERMISSIONS.BUSINESS_APPROVER,
};

export const SOURCE_TYPE = {
  CLIENT: 'client',
  TRATO: 'trato',
  PAID_BY_RAPPI: 'paid_by_rappi',
  NONE: '',
} as const;
export type SourceType = typeof SOURCE_TYPE[keyof typeof SOURCE_TYPE];

// Display names for source types as returned by the API (po.source_type field).
export const SOURCE_TYPE_LABEL = {
  [SOURCE_TYPE.PAID_BY_RAPPI]: 'Paid by Rappi',
  [SOURCE_TYPE.CLIENT]:        'Client',
  [SOURCE_TYPE.TRATO]:         'Trato',
} as const;

// PR #38: companies with is_oc_required=true are not eligible for the Trato flow. Shown as the
// tooltip on disabled options in the Company-to-bill selector.
export const COMPANY_OC_REQUIRED_TOOLTIP = 'Not available for Trato';

// Placeholder shown for fields that have no value (e.g. backend has not populated them yet, or
// the field is not applicable for the current source type such as Paid by Rappi).
export const PO_EMPTY_VALUE = '-';

// PR #38: error key set on the PO External ID control/validator when the availability endpoint
// reports a conflict. Surfaced below the input via the appFormErrors directive.
export const PO_EXTERNAL_ID_ERROR = {
  CONFLICT: 'externalIdConflict',
} as const;

export const PO_OWNER_ERROR = {
  BANNER: "This action can't be completed because the owner {{owner_email}} doesn't exist in Zoho.",
} as const;

export const PO_SUCCESS_MESSAGE = {
  DRAFT:  'Purchase order saved as draft.',
  SUBMIT: 'Purchase order submitted successfully.',
  UPDATE: 'Purchase order updated successfully.',
} as const;

export const PO_FIELD = {
  PURCHASE_ORDER_NAME:      'purchase_order_name',
  SOURCE_TYPE:              'source_type',
  MAKER_TO_EXECUTE:         'maker_to_execute',
  MAKER_NAME:               'maker_name',
  PO_TYPE:                  'po_type',
  OWNER:                    'owner',
  PARENT_PO:                'parent_po',
  DOCUMENT_FILE:            'document_file',
  PO_LINK:                  'po_link',
  PO_BUDGET:                'po_budget',
  PO_CURRENCY:              'po_currency',
  CO_INVESTMENT:            'co_investment',
  RE_INVESTMENT:            're_investment',
  PO_NUMBER:                'po_number',
  TAX_PERCENTAGE:           'tax_percentage',
  COMPANY_TO_BILL:          'company_to_bill',
  POINT_OF_CONTACT:         'point_of_contact',
  AGENCY:                   'agency',
  AGENCY_RECOGNITION_METHOD:'agency_recognition_method',
  REQUEST_REASON:           'request_reason',
  TAGS:                     'tags',
  DETAIL_BRIEF:             'detail_brief',
} as const;
export type PoField = typeof PO_FIELD[keyof typeof PO_FIELD];

export const PO_SECTION = {
  GENERAL_INFORMATION:      'general_information',
  PURCHASE_ORDER_DOCUMENT:  'purchase_order_document',
  BUDGET_AND_DATES:         'budget_and_dates',
  BUYER_INFORMATION:        'buyer_information',
  ADDITIONAL_INFO:          'additional_info',
} as const;
export type PoSection = typeof PO_SECTION[keyof typeof PO_SECTION];

export const SEARCH_SELECT_FIELDS = [
  PO_FIELD.COMPANY_TO_BILL,
  PO_FIELD.POINT_OF_CONTACT,
  PO_FIELD.AGENCY,
  PO_FIELD.OWNER,
] as const;

export const INVESTMENT_FIELDS = [
  PO_FIELD.CO_INVESTMENT,
  PO_FIELD.RE_INVESTMENT,
] as const;
export type InvestmentField = typeof INVESTMENT_FIELDS[number];

export const PO_SECTION_LABELS: Record<PoSection, string> = {
  [PO_SECTION.GENERAL_INFORMATION]:     'General information',
  [PO_SECTION.PURCHASE_ORDER_DOCUMENT]: 'Purchase order document',
  [PO_SECTION.BUDGET_AND_DATES]:        'Budget & dates',
  [PO_SECTION.BUYER_INFORMATION]:       'Buyer information',
  [PO_SECTION.ADDITIONAL_INFO]:         'Additional info',
};

export const PO_FIELD_LABELS: Record<PoField, string> = {
  [PO_FIELD.PURCHASE_ORDER_NAME]:       'Purchase order name',
  [PO_FIELD.SOURCE_TYPE]:               'Source type',
  [PO_FIELD.MAKER_TO_EXECUTE]:          'Maker to execute',
  [PO_FIELD.MAKER_NAME]:               'Maker name',
  [PO_FIELD.PO_TYPE]:                   'PO type',
  [PO_FIELD.OWNER]:                     'Rappi Owner',
  [PO_FIELD.PARENT_PO]:                 'Parent PO (Opcional)',
  [PO_FIELD.DOCUMENT_FILE]:             'Purchase order document',
  [PO_FIELD.PO_LINK]:                   'PO Link',
  [PO_FIELD.PO_BUDGET]:                 'PO budget',
  [PO_FIELD.PO_CURRENCY]:               'PO currency',
  [PO_FIELD.CO_INVESTMENT]:             'Co-investment % (Opcional)',
  [PO_FIELD.RE_INVESTMENT]:             'Re-investment % (Opcional)',
  [PO_FIELD.PO_NUMBER]:                 'PO External ID',
  [PO_FIELD.TAX_PERCENTAGE]:            'Tax %',
  [PO_FIELD.COMPANY_TO_BILL]:           'Company to bill',
  [PO_FIELD.POINT_OF_CONTACT]:          'Point of contact',
  [PO_FIELD.AGENCY]:                    'Agency',
  [PO_FIELD.AGENCY_RECOGNITION_METHOD]: 'Revenue Recognition Method',
  [PO_FIELD.REQUEST_REASON]:            'Request reason',
  [PO_FIELD.TAGS]:                      'Tags (Opcional)',
  [PO_FIELD.DETAIL_BRIEF]:              'Detail / brief',
};

// HU3 — PO status enum. The `statuses` filter accepts the codes (snake_case); the list/detail
// responses return the legible labels. See docs/hu3-listado-detalle-endpoint.md.
export const PO_STATUS = {
  DRAFT:                       'draft',
  WAITING_FOR_TRATO:          'waiting_for_trato',
  TRATO_FAILED:               'trato_failed',
  WAITING_FOR_SIGNATURE:      'waiting_for_signature',
  WAITING_FOR_SETTLEMENT:     'waiting_for_settlement',
  SETTLEMENT_FAILED:          'settlement_failed',
  WAITING_FOR_FINANCE:        'waiting_for_finance',
  WAITING_FOR_KAM:            'waiting_for_kam',
  WAITING_FOR_APPROVAL:       'waiting_for_approval',
  APPROVED:                   'approved',
  CLOSED:                     'closed',
  CANCELED:                   'canceled',
} as const;
export type PoStatus = typeof PO_STATUS[keyof typeof PO_STATUS];

export const PO_STATUS_LABELS: Record<PoStatus, string> = {
  [PO_STATUS.DRAFT]:                          'Draft',
  [PO_STATUS.WAITING_FOR_TRATO]:             'Waiting for Trato',
  [PO_STATUS.TRATO_FAILED]:                  'Trato Failed',
  [PO_STATUS.WAITING_FOR_SIGNATURE]:      'Waiting for Signature',
  [PO_STATUS.WAITING_FOR_SETTLEMENT]:        'Waiting for Settlement',
  [PO_STATUS.SETTLEMENT_FAILED]:             'Settlement Failed',
  [PO_STATUS.WAITING_FOR_FINANCE]:           'Waiting for Finance',
  [PO_STATUS.WAITING_FOR_KAM]:               'Waiting for KAM',
  [PO_STATUS.WAITING_FOR_APPROVAL]: 'Waiting for Approval',
  [PO_STATUS.APPROVED]:                       'Approved',
  [PO_STATUS.CLOSED]:                         'Closed',
  [PO_STATUS.CANCELED]:                       'Canceled',
};

// Status informational banner colors (Figma nodes 931:48861, 931:49592, 875:49322, 920:46251, 506:34980).
// Messages come from the CMS config (FinancesConfig.status_banners); colors are design tokens.
export const PO_STATUS_BANNER: Partial<Record<PoStatus, { bg: string; iconColor: string }>> = {
  [PO_STATUS.WAITING_FOR_TRATO]:      { bg: '#FFF3DA', iconColor: '#AD832E' },
  [PO_STATUS.WAITING_FOR_SIGNATURE]:  { bg: '#E7E3F4', iconColor: '#624AA4' },
  [PO_STATUS.TRATO_FAILED]:           { bg: '#FFE9E9', iconColor: '#FF441F' },
  [PO_STATUS.WAITING_FOR_SETTLEMENT]: { bg: '#FFDEBE', iconColor: '#FF7D00' },
  [PO_STATUS.SETTLEMENT_FAILED]:      { bg: '#FFE9E9', iconColor: '#FF441F' },
};

// Inverse lookup: response label → status code (for CSS badge classes keyed by code).
export const PO_LABEL_TO_CODE: Record<string, PoStatus> = Object.fromEntries(
  (Object.entries(PO_STATUS_LABELS) as [PoStatus, string][]).map(([code, label]) => [label, code]),
) as Record<string, PoStatus>;

// ── Approval actions ──────────────────────────────────────────────────────────

// Approval action identifiers — used by the po-approval-actions component outputs and any
// analytics/logging that needs to distinguish which approval button was clicked.
export const PO_APPROVAL_ACTION = {
  RESUBMIT:       'resubmit_for_approval',
  APPROVE:        'approve_purchase_order',
  RETURN_TO_KAM:  'return_to_kam',
} as const;
export type PoApprovalAction = typeof PO_APPROVAL_ACTION[keyof typeof PO_APPROVAL_ACTION];

// PO statuses in which the KAM can re-submit the purchase order for approval.
export const PO_RESUBMIT_STATUSES: readonly PoStatus[] = [
  PO_STATUS.DRAFT,
  PO_STATUS.TRATO_FAILED,
  PO_STATUS.SETTLEMENT_FAILED,
  PO_STATUS.WAITING_FOR_KAM,
];

// PO statuses in which a finance approver can approve or return the purchase order.
export const PO_FINANCE_APPROVAL_STATUSES: readonly PoStatus[] = [
  PO_STATUS.WAITING_FOR_FINANCE,
];

// ── Cancel PO ────────────────────────────────────────────────────────────────

// PO stages from which the PO can be canceled (pre-approval only).
// See POST /purchase-orders/:id/cancel — "Stage cancelable" rule.
export const PO_CANCEL_STATUSES: readonly PoStatus[] = [
  PO_STATUS.DRAFT,
  PO_STATUS.WAITING_FOR_TRATO,
  PO_STATUS.TRATO_FAILED,
  PO_STATUS.WAITING_FOR_SIGNATURE,
  PO_STATUS.SETTLEMENT_FAILED,
  PO_STATUS.WAITING_FOR_FINANCE,
  PO_STATUS.WAITING_FOR_KAM,
  PO_STATUS.WAITING_FOR_APPROVAL,
];

// Source types that support cancellation.
export const PO_CANCEL_SOURCE_TYPES: readonly string[] = [
  SOURCE_TYPE.TRATO,
  SOURCE_TYPE.CLIENT,
  SOURCE_TYPE.PAID_BY_RAPPI,
];

// ── Confirmation dialog config (reusable) ───────────────────────────────────

// Dialog action types — each type has its own config in PO_DIALOG_CONFIG.
export const PO_DIALOG_TYPE = {
  CANCEL:        'cancel',
  LOCK:          'lock',
  UNLOCK:        'unlock',
  APPROVE:       'approve',
  RETURN_TO_KAM: 'return_to_kam',
} as const;
export type PoDialogType = typeof PO_DIALOG_TYPE[keyof typeof PO_DIALOG_TYPE];

// Icon identifiers rendered inside the dialog body circle.
export const PO_DIALOG_ICON = {
  TRASH:    'trash',
  LOCK:     'lock',
  UNLOCK:   'unlock',
  APPROVE:  'approve',
} as const;
export type PoDialogIcon = typeof PO_DIALOG_ICON[keyof typeof PO_DIALOG_ICON];

export interface PoDialogConfig {
  title:              string;
  message:            string;
  goBack:             string;
  confirm:            string;
  icon:               PoDialogIcon;
  iconColor:          string;
  textAreaPlaceholder?: string;
}

// Dialog configuration per type (Figma node 517:48563).
export const PO_DIALOG_CONFIG: Record<PoDialogType, PoDialogConfig> = {
  [PO_DIALOG_TYPE.CANCEL]: {
    title:     'Cancel purchase order?',
    message:   'This action cannot be undone.',
    goBack:    'Go back',
    confirm:   'Cancel PO',
    icon:      PO_DIALOG_ICON.TRASH,
    iconColor: '#FF2525',
  },
  [PO_DIALOG_TYPE.LOCK]: {
    title:     'Block Purchase Order??',
    message:   'This action will transition the PO to Closed and block further execution.',
    goBack:    'Go back',
    confirm:   'Close PO',
    icon:      PO_DIALOG_ICON.LOCK,
    iconColor: '#4589FF',
  },
  [PO_DIALOG_TYPE.UNLOCK]: {
    title:     'Unlock purchase order?',
    message:   'This action will restore the PO to Approved and allow further execution.',
    goBack:    'Go back',
    confirm:   'Restore PO',
    icon:      PO_DIALOG_ICON.UNLOCK,
    iconColor: '#4589FF',
  },
  [PO_DIALOG_TYPE.APPROVE]: {
    title:     'Approve this Purchase Order?',
    message:   'Once approved, this order will be available for execution. Make sure all amounts and documentation are correct before proceeding.',
    goBack:    'Cancel',
    confirm:   'Yes, approve',
    icon:      PO_DIALOG_ICON.APPROVE,
    iconColor: '#4589FF',
  },
  [PO_DIALOG_TYPE.RETURN_TO_KAM]: {
    title:              'Return to KAM',
    message:            '',
    goBack:             'Cancel',
    confirm:            'Confirm return',
    icon:               PO_DIALOG_ICON.TRASH,
    iconColor:          '#FF2525',
    textAreaPlaceholder: 'Describe the reason for returning this PO to the KAM',
  },
};
